import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { RefreshCw, AlertTriangle, AlertCircle, Clock, Info, User, Calendar, MapPin, Settings, Plus, MessageSquare } from 'lucide-react';
import axios from 'axios';

interface Ticket {
  id: number;
  ticketNumber: string;
  title: string;
  description: string;
  categoryId: number | null;
  categoryName: string | null;
  priority: string;
  status: string;
  location: string | null;
  classroomId: number | null;
  classroomName: string | null;
  equipmentId: number | null;
  equipmentName: string | null;
  reportedById: number;
  reportedByName: string;
  assignedToId: number | null;
  assignedToName: string | null;
  assignedAt: string | null;
  slaResponseDue: string | null;
  slaResolutionDue: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface MaintenanceUser {
  id: number;
  full_name: string;
}

interface TicketStats {
  open: number;
  inProgress: number;
  waiting: number;
  resolved: number;
  closed: number;
  critical: number;
  high: number;
  overdue: number;
}

const priorityConfig: Record<string, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  P0: { label: 'Crítico', color: 'text-red-700', bgColor: 'bg-red-50 border-red-200', icon: <AlertTriangle className="w-3 h-3" /> },
  P1: { label: 'Alto', color: 'text-orange-700', bgColor: 'bg-orange-50 border-orange-200', icon: <AlertCircle className="w-3 h-3" /> },
  P2: { label: 'Medio', color: 'text-yellow-700', bgColor: 'bg-yellow-50 border-yellow-200', icon: <Clock className="w-3 h-3" /> },
  P3: { label: 'Bajo', color: 'text-green-700', bgColor: 'bg-green-50 border-green-200', icon: <Info className="w-3 h-3" /> },
};

const statusColumns = [
  { key: 'abierto', label: 'Abiertos', color: 'bg-blue-500' },
  { key: 'en_curso', label: 'En Curso', color: 'bg-yellow-500' },
  { key: 'en_espera', label: 'En Espera', color: 'bg-gray-500' },
  { key: 'resuelto', label: 'Resueltos', color: 'bg-green-500' },
];

export default function GestionTickets() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [maintenanceUsers, setMaintenanceUsers] = useState<MaintenanceUser[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState<number | null>(null);
  const [updatingTicket, setUpdatingTicket] = useState(false);
  const [draggedTicketId, setDraggedTicketId] = useState<number | null>(null);
  
  const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{ ticketId: number; ticket: Ticket; newStatus: string } | null>(null);
  const [statusComment, setStatusComment] = useState('');
  const [statusChangeAssignee, setStatusChangeAssignee] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const handleDragStart = (e: React.DragEvent, ticketId: number) => {
    setDraggedTicketId(ticketId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ticketId.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    const ticketId = draggedTicketId || parseInt(e.dataTransfer.getData('text/plain'), 10);
    
    if (!ticketId || isNaN(ticketId)) return;
    
    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket || ticket.status === newStatus) {
      setDraggedTicketId(null);
      return;
    }

    setPendingStatusChange({ ticketId, ticket, newStatus });
    setStatusChangeAssignee(ticket.assignedToId);
    setStatusComment('');
    setStatusChangeModalOpen(true);
    setDraggedTicketId(null);
  };

  const handleDragEnd = () => {
    setDraggedTicketId(null);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ticketsRes, usersRes, statsRes] = await Promise.all([
        axios.get('/api/tickets'),
        axios.get('/api/tickets/maintenance-users'),
        axios.get('/api/tickets/stats/summary'),
      ]);
      setTickets(ticketsRes.data);
      setMaintenanceUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId: number, newStatus: string, comment?: string, assigneeId?: number | null) => {
    setUpdatingTicket(true);
    try {
      const updateData: any = { status: newStatus };
      if (comment) {
        updateData.statusComment = comment;
      }
      if (assigneeId !== undefined) {
        updateData.assignedToId = assigneeId;
      }
      await axios.put(`/api/tickets/${ticketId}`, updateData);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error al actualizar ticket');
    } finally {
      setUpdatingTicket(false);
    }
  };

  const confirmStatusChange = async () => {
    if (!pendingStatusChange) return;
    await updateTicketStatus(
      pendingStatusChange.ticketId, 
      pendingStatusChange.newStatus, 
      statusComment,
      statusChangeAssignee
    );
    setStatusChangeModalOpen(false);
    setPendingStatusChange(null);
    setStatusComment('');
    setStatusChangeAssignee(null);
  };

  const cancelStatusChange = () => {
    setStatusChangeModalOpen(false);
    setPendingStatusChange(null);
    setStatusComment('');
    setStatusChangeAssignee(null);
  };

  const assignTicket = async () => {
    if (!selectedTicket) return;
    setUpdatingTicket(true);
    try {
      await axios.put(`/api/tickets/${selectedTicket.id}`, { 
        assignedToId: selectedAssignee,
        status: selectedTicket.status === 'abierto' ? 'en_curso' : selectedTicket.status
      });
      setAssignModalOpen(false);
      setSelectedTicket(null);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error al asignar ticket');
    } finally {
      setUpdatingTicket(false);
    }
  };

  const getTicketsByStatus = (status: string) => {
    return tickets.filter(t => t.status === status).sort((a, b) => {
      const priorityOrder = ['P0', 'P1', 'P2', 'P3'];
      return priorityOrder.indexOf(a.priority) - priorityOrder.indexOf(b.priority);
    });
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `hace ${diffDays}d`;
    if (diffHours > 0) return `hace ${diffHours}h`;
    if (diffMins > 0) return `hace ${diffMins}m`;
    return 'ahora';
  };

  const TicketCard = ({ ticket }: { ticket: Ticket }) => {
    const priority = priorityConfig[ticket.priority] || priorityConfig.P2;
    const isOverdue = ticket.slaResolutionDue && new Date(ticket.slaResolutionDue) < new Date() && !['resuelto', 'cerrado'].includes(ticket.status);
    const isDragging = draggedTicketId === ticket.id;

    return (
      <div 
        draggable
        onDragStart={(e) => handleDragStart(e, ticket.id)}
        onDragEnd={handleDragEnd}
        className={`bg-white rounded-lg border p-3 shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing ${isOverdue ? 'border-red-300 bg-red-50' : ''} ${isDragging ? 'opacity-50 scale-95' : ''}`}
        onClick={() => setSelectedTicket(ticket)}
      >
        <div className="flex justify-between items-start mb-2">
          <span className="text-xs font-mono text-gray-500">{ticket.ticketNumber}</span>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${priority.bgColor} ${priority.color}`}>
            {priority.icon}
            {ticket.priority}
          </span>
        </div>
        <h4 className="font-medium text-sm mb-2 line-clamp-2">{ticket.title}</h4>
        <div className="space-y-1 text-xs text-gray-500">
          {ticket.categoryName && (
            <div className="flex items-center gap-1">
              <Settings className="w-3 h-3" />
              {ticket.categoryName}
            </div>
          )}
          {(ticket.location || ticket.classroomName) && (
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {ticket.location || ticket.classroomName}
            </div>
          )}
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatTimeAgo(ticket.createdAt)}
          </div>
        </div>
        {ticket.assignedToName ? (
          <div className="mt-2 pt-2 border-t flex items-center gap-1 text-xs">
            <User className="w-3 h-3 text-blue-500" />
            <span className="text-blue-600">{ticket.assignedToName}</span>
          </div>
        ) : (
          <div className="mt-2 pt-2 border-t">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTicket(ticket);
                setAssignModalOpen(true);
              }}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <User className="w-3 h-3" />
              Asignar
            </button>
          </div>
        )}
        {isOverdue && (
          <div className="mt-2 text-xs text-red-600 font-medium flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            SLA vencido
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold">Gestión de Tickets</h1>
          <p className="text-gray-500">Tablero Kanban de incidencias</p>
        </div>
        <Button onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mb-4">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.open}</div>
            <div className="text-xs text-blue-600">Abiertos</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-yellow-600">{stats.inProgress}</div>
            <div className="text-xs text-yellow-600">En Curso</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-600">{stats.waiting}</div>
            <div className="text-xs text-gray-600">En Espera</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.resolved}</div>
            <div className="text-xs text-green-600">Resueltos</div>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-red-600">{stats.critical}</div>
            <div className="text-xs text-red-600">Críticos</div>
          </div>
          <div className="bg-orange-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-orange-600">{stats.high}</div>
            <div className="text-xs text-orange-600">Urgentes</div>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center col-span-2">
            <div className="text-2xl font-bold text-red-600">{stats.overdue}</div>
            <div className="text-xs text-red-600">SLA Vencido</div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-x-auto">
        <div className="flex gap-4 h-full min-w-max">
          {statusColumns.map(({ key, label, color }) => {
            const columnTickets = getTicketsByStatus(key);
            const isDropTarget = draggedTicketId !== null;
            return (
              <div 
                key={key} 
                className={`w-80 flex flex-col bg-gray-50 rounded-lg transition-all ${isDropTarget ? 'ring-2 ring-blue-300 ring-opacity-50' : ''}`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, key)}
              >
                <div className="p-3 border-b bg-white rounded-t-lg">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${color}`}></div>
                    <span className="font-medium">{label}</span>
                    <span className="text-gray-400 text-sm">({columnTickets.length})</span>
                  </div>
                </div>
                <div 
                  className={`flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-320px)] min-h-[100px] ${isDropTarget ? 'bg-blue-50 bg-opacity-30' : ''}`}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, key)}
                >
                  {columnTickets.map(ticket => (
                    <TicketCard key={ticket.id} ticket={ticket} />
                  ))}
                  {columnTickets.length === 0 && (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      {isDropTarget ? 'Soltar aquí' : 'Sin tickets'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedTicket && !assignModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="text-sm text-gray-500 font-mono">{selectedTicket.ticketNumber}</div>
                <h2 className="text-xl font-semibold">{selectedTicket.title}</h2>
              </div>
              <div className="flex gap-2">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${priorityConfig[selectedTicket.priority]?.bgColor} ${priorityConfig[selectedTicket.priority]?.color}`}>
                  {priorityConfig[selectedTicket.priority]?.icon}
                  {selectedTicket.priority}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Descripción</label>
                <p className="mt-1 text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Categoría</label>
                  <p className="mt-1">{selectedTicket.categoryName || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Ubicación</label>
                  <p className="mt-1">{selectedTicket.location || selectedTicket.classroomName || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Equipo</label>
                  <p className="mt-1">{selectedTicket.equipmentName || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Reportado por</label>
                  <p className="mt-1">{selectedTicket.reportedByName}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500 block mb-2">Asignar a</label>
                <select
                  value={selectedTicket.assignedToId || ''}
                  onChange={(e) => {
                    const userId = e.target.value ? parseInt(e.target.value, 10) : null;
                    setSelectedAssignee(userId);
                  }}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Sin asignar</option>
                  {maintenanceUsers.map(user => (
                    <option key={user.id} value={user.id}>{user.full_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500 block mb-2">Cambiar Estado</label>
                <div className="flex gap-2 flex-wrap">
                  {statusColumns.map(({ key, label, color }) => (
                    <button
                      key={key}
                      onClick={() => updateTicketStatus(selectedTicket.id, key)}
                      disabled={updatingTicket || selectedTicket.status === key}
                      className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
                        selectedTicket.status === key 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      <div className={`w-2 h-2 rounded-full ${color}`}></div>
                      {label}
                    </button>
                  ))}
                  <button
                    onClick={() => updateTicketStatus(selectedTicket.id, 'cerrado')}
                    disabled={updatingTicket}
                    className="px-3 py-2 rounded-lg text-sm bg-slate-600 text-white hover:bg-slate-700"
                  >
                    Cerrar Ticket
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-between mt-6 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  if (selectedAssignee !== null && selectedAssignee !== selectedTicket.assignedToId) {
                    assignTicket();
                  }
                }}
                disabled={selectedAssignee === null || selectedAssignee === selectedTicket.assignedToId || updatingTicket}
              >
                Guardar Asignación
              </Button>
              <Button variant="outline" onClick={() => { setSelectedTicket(null); setSelectedAssignee(null); }}>
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {assignModalOpen && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Asignar Ticket</h3>
            <p className="text-gray-600 mb-4">Ticket: {selectedTicket.ticketNumber}</p>
            
            <select
              value={selectedAssignee || ''}
              onChange={(e) => setSelectedAssignee(parseInt(e.target.value, 10))}
              className="w-full border rounded-lg px-3 py-2 mb-4"
            >
              <option value="">Seleccionar técnico...</option>
              {maintenanceUsers.map(user => (
                <option key={user.id} value={user.id}>{user.full_name}</option>
              ))}
            </select>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setAssignModalOpen(false); setSelectedTicket(null); }}>
                Cancelar
              </Button>
              <Button onClick={assignTicket} disabled={!selectedAssignee || updatingTicket}>
                {updatingTicket ? 'Asignando...' : 'Asignar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {statusChangeModalOpen && pendingStatusChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold">Cambiar Estado de Ticket</h3>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <div className="text-sm text-gray-500 font-mono">{pendingStatusChange.ticket.ticketNumber}</div>
              <div className="font-medium">{pendingStatusChange.ticket.title}</div>
              <div className="text-sm mt-2 flex items-center gap-2">
                <span className="text-gray-500">Estado:</span>
                <span className="px-2 py-0.5 rounded bg-gray-200 text-xs">{pendingStatusChange.ticket.status}</span>
                <span className="text-gray-400">→</span>
                <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-medium">{pendingStatusChange.newStatus}</span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsable
                </label>
                <select
                  value={statusChangeAssignee || ''}
                  onChange={(e) => setStatusChangeAssignee(e.target.value ? parseInt(e.target.value, 10) : null)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Sin asignar</option>
                  {maintenanceUsers.map(user => (
                    <option key={user.id} value={user.id}>{user.full_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comentario (opcional)
                </label>
                <textarea
                  value={statusComment}
                  onChange={(e) => setStatusComment(e.target.value)}
                  placeholder="Agregar comentario sobre el cambio de estado..."
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={cancelStatusChange} disabled={updatingTicket}>
                Cancelar
              </Button>
              <Button onClick={confirmStatusChange} disabled={updatingTicket}>
                {updatingTicket ? 'Guardando...' : 'Confirmar Cambio'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
